import Link from 'next/link';
import { clearSavedUserFilter } from '@/clientOnlyUtils/clientUtils';
import { FirebaseError } from 'firebase/app';
import { createUserWithEmailAndPassword, getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import React, { useActionState } from 'react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { toast } from 'react-hot-toast';

import type { SelectOption } from '@poseidon/components/Input2';
import { isNotEmpty } from '@poseidon/components/common';
import { Button, Input, Label, PasswordInput, Select } from '@poseidon/components/Input2';
import { Communication, Security, Users } from '@poseidon/components/StrokeIcons';
import { WaveSpinner } from '@poseidon/components/WaveSpinner';

import { useRecordSignUpDataMutation } from '@/hooks/trpc/useUserMutation';
import { GoogleIcon } from '~/components/CompanyIcons';
import { useOnboardingContext } from '~/components/Onboarding/onboarding.context';
import { LogoIcon } from '~/components/ToneIcons';
import { env } from '~/env';
import { AuthCard, AuthLayout } from '~/pages/login';
import { type NextPageWithLayout } from './_app';

export const ATTRIBUTION_VALUES = ['Paid Media', 'Search', 'My agency uses it', 'G2', 'Customer reference'] as const;

export type AttributionValue = (typeof ATTRIBUTION_VALUES)[number];

export const attributionOptions: SelectOption[] = ATTRIBUTION_VALUES.map((value) => ({
  label: value,
  value,
}));

const validAttributionValues = new Set(ATTRIBUTION_VALUES);

// Type guard to check if a string is a valid attribution value
function isValidAttribution(value: string): value is AttributionValue {
  return validAttributionValues.has(value as AttributionValue);
}

type FormState = {
  errors?: {
    name?: string;
    email?: string;
    password?: string;
    attribution?: string;
    recaptcha?: string;
    root?: string;
  };
  name?: string;
  email?: string;
  password?: string;
  attribution?: string;
};

const SignupForm = () => {
  const recordSignUpDataMutation = useRecordSignUpDataMutation();
  const [, { resetOnboarding }] = useOnboardingContext();
  const [loadingWithGoogle, setLoadingWithGoogle] = React.useState(false);
  const [recaptchaReady, setRecaptchaReady] = React.useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Track when reCAPTCHA is ready
  React.useEffect(() => {
    if (executeRecaptcha) {
      setRecaptchaReady(true);
    }
  }, [executeRecaptcha]);

  // Helper function to verify reCAPTCHA with proper error handling
  const verifyRecaptcha = async (action: string): Promise<string | null> => {
    if (!executeRecaptcha) {
      return null;
    }

    try {
      return executeRecaptcha(action);
    } catch {
      return null;
    }
  };

  const [state, submitAction, isPending] = useActionState<FormState | undefined, FormData>(async (_, formData) => {
    const emailValue = formData.get('email');
    const passwordValue = formData.get('password');
    const nameValue = formData.get('name');
    const attributionValue = formData.get('attribution');

    const email = emailValue instanceof Object ? undefined : emailValue?.toString();
    const password = passwordValue instanceof Object ? undefined : passwordValue?.toString();
    const name = nameValue instanceof Object ? undefined : nameValue?.toString();
    const attribution = attributionValue instanceof Object ? undefined : attributionValue?.toString();

    // Verify reCAPTCHA
    const recaptchaToken = await verifyRecaptcha('signup');

    if (!recaptchaToken) {
      return { errors: { recaptcha: 'ReCAPTCHA verification failed. Please try again.' }, name, email, attribution };
    }

    if (!isNotEmpty(name)) {
      return { errors: { name: 'Oops - Please tell us who you are?' }, name, email, attribution };
    }
    if (!isNotEmpty(email)) {
      return { errors: { email: 'We need your email to get started' }, name, email, attribution };
    }
    if (!isNotEmpty(password)) {
      return { errors: { password: 'Invalid password' }, name, email, attribution };
    }

    if (password.length < 8) {
      return { errors: { password: 'Password have to be longer' }, name, email, attribution };
    }

    if (!isNotEmpty(attribution)) {
      return { errors: { attribution: 'This field is required' }, name, email, attribution };
    }

    if (!isValidAttribution(attribution)) {
      return { errors: { attribution: 'Please select a valid option' }, name, email, attribution };
    }

    // TypeScript now knows attribution is AttributionValue due to type guard above

    try {
      if (email != null && password != null) {
        await createUserWithEmailAndPassword(getAuth(), email, password);
        await recordSignUpDataMutation.mutateAsync({
          name,
          attribution,
        });
        clearSavedUserFilter();

        // Clear any previous onboarding state for new users
        localStorage.removeItem('onboardingCompleted');
        localStorage.removeItem('selectedTier');
        localStorage.removeItem('icpFormData');

        resetOnboarding();
      }
    } catch (error: unknown) {
      if (error instanceof FirebaseError) {
        console.error('Firebase signup error:', error.code, error.message);
        if (error.code === 'auth/wrong-password') {
          return { errors: { password: 'Invalid password' }, name, email, attribution };
        } else if (error.code === 'auth/invalid-email') {
          return {
            errors: { email: 'Please check email format or consider another address.' },
            name,
            email,
            attribution,
          };
        } else if (error.code === 'auth/user-not-found') {
          return { errors: { root: 'Failed to login' }, name, email, attribution };
        } else if (error.code === 'auth/too-many-requests') {
          return {
            errors: { root: 'Too many failed login attempts, please wait a few minutes' },
            name,
            email,
            attribution,
          };
        } else if (error.code === 'auth/email-already-in-use') {
          return {
            errors: { root: 'This email is already registered. Please log in or use a different email.' },
            name,
            email,
            attribution,
          };
        } else {
          return { errors: { root: 'Failed to login' }, name, email, attribution };
        }
      }
    }
  }, undefined);

  const loginWithGoogle = async () => {
    const auth = getAuth();
    const provider = new GoogleAuthProvider();
    try {
      setLoadingWithGoogle(true);

      // Verify reCAPTCHA before Google sign-in
      const recaptchaToken = await verifyRecaptcha('google_signup');

      if (!recaptchaToken) {
        toast.error('ReCAPTCHA verification failed. Please try again.');
        setLoadingWithGoogle(false);
        return;
      }

      await signInWithPopup(auth, provider);
      clearSavedUserFilter();

      // Clear any previous onboarding state for new users
      localStorage.removeItem('onboardingCompleted');
      localStorage.removeItem('selectedTier');
      localStorage.removeItem('icpFormData');

      resetOnboarding();
    } catch (e: unknown) {
      // COOP (Cross-Origin-Opener-Policy) can block the popup from communicating
      // back, causing signInWithPopup to error even though auth succeeded.
      if (auth.currentUser) {
        clearSavedUserFilter();
        window.location.reload();
        return;
      }

      if (e instanceof FirebaseError) {
        if (e.code === 'auth/popup-closed-by-user') {
          toast.error('Looks like you closed the popup. Please try again.');
        } else if (e.code === 'auth/too-many-requests') {
          toast.error('Too many failed login attempts, please wait a few minutes');
        } else if (e.code === 'auth/unauthorized-domain') {
          toast.error(
            <span>
              Looks like the domain you are using is not authorized. Please visit{' '}
              <b>
                <a href="https://useposeidon.com/login">https://useposeidon.com/login</a>
              </b>{' '}
              to signup.
            </span>,
          );
        } else {
          console.log(e.code, '->', e.message);
          toast.error('Oops! We could not log you in. Please try again');
        }
      } else {
        toast.error('Oops! We could not log you in. Please try again');
      }
    } finally {
      setLoadingWithGoogle(false);
    }
  };

  console.log('state?.attribution', state?.attribution);

  return (
    <AuthLayout>
      <AuthCard>
        <LogoIcon className="size-12 text-white" />
        <h2 className="mt-8 font-(family-name:--display-font) text-3xl font-bold tracking-tight text-white">Sign up</h2>
        <p className="my-4 mb-8 text-base text-white/60">Dive right into Prospecting</p>
        <form className="flex w-full flex-col items-stretch gap-4" action={submitAction}>
          <div className="flex flex-col gap-1">
            <Label className="text-white/80">Name</Label>
            <Input
              name="name"
              icon={<Users.Users01 />}
              placeholder="Enter your name"
              error={state?.errors?.name}
              defaultValue={state?.name}
              size="lg"
              className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:bg-white/15! focus-within:border-white/40! focus-within:outline-hidden! hover:bg-white/15!"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-white/80">Email</Label>
            <Input
              name="email"
              icon={<Communication.Mail01 />}
              placeholder="Enter your email"
              error={state?.errors?.email}
              defaultValue={state?.email}
              size="lg"
              className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:bg-white/15! focus-within:border-white/40! focus-within:outline-hidden! hover:bg-white/15!"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-white/80">Password</Label>
            <p className="text-xs text-white/50">Must be at least 8 characters.</p>
            <PasswordInput
              name="password"
              icon={<Security.Key01 />}
              placeholder="Create a password"
              error={state?.errors?.password}
              defaultValue={state?.password}
              size="lg"
              className="border-white/20! bg-white/10! text-white placeholder:text-white/40 focus-within:border-white/40! focus-within:bg-white/15! hover:bg-white/15! [&>.w-\[1px\]]:bg-white/20!"
            />
          </div>

          <div className="flex flex-col gap-1">
            <Label className="text-white/80">How'd you hear about us?</Label>
            <Select
              name="attribution"
              placeholder="Select an option"
              error={state?.errors?.attribution}
              options={attributionOptions}
              defaultValue={state?.attribution}
              size="lg"
            />
          </div>

          {state?.errors?.recaptcha != null && <div className="text-fair-pink-600">{state.errors.recaptcha}</div>}
          {state?.errors?.root != null && <div className="text-fair-pink-600">{state.errors.root}</div>}
          {!recaptchaReady && <div className="text-sm text-white/50">Loading security verification...</div>}
          <Button
            type="submit"
            color="blue"
            size="lg"
            textAlign="center"
            disabled={isPending || !recaptchaReady}
            icon={isPending ? <WaveSpinner /> : undefined}>
            Sign up
          </Button>

          <div className="text-center text-sm font-semibold text-white/50">or</div>
          <Button
            type="button"
            size="lg"
            textAlign="center"
            disabled={loadingWithGoogle || isPending || !recaptchaReady}
            icon={loadingWithGoogle ? <WaveSpinner /> : <GoogleIcon />}
            className="border-white/20 bg-white/10 text-white hover:bg-white/15"
            onClick={loginWithGoogle}>
            Signup with google
          </Button>
          <div className="text-center text-sm text-white/60 [&_a:hover]:text-white [&_a]:text-white/60">
            Already have an account?&nbsp;
            <Link href="/login">Log in</Link>
          </div>

          <p className="m-auto flex max-w-[65%] flex-wrap justify-center text-white/50 [&_a:hover]:text-white [&_a]:text-white/60">
            By Signing up, you agree to our
            <a target="_blank" href="https://www.iubenda.com/terms-and-conditions/48376028">
              Terms of Service
            </a>
            and
            <a target="_blank" href="https://www.iubenda.com/privacy-policy/48376028">
              Privacy Policy
            </a>
            .
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};

const SignupPage: NextPageWithLayout = () => {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
      scriptProps={{ appendTo: 'head', async: true, defer: true }}>
      <SignupForm />
    </GoogleReCaptchaProvider>
  );
};

SignupPage.authParams = {
  redirect: '/onboarding',
};

SignupPage.layout = 'onlyIfNotLoggedIn';
export default SignupPage;
